<!--
  An input to write script
  Using weird contenteditable HTML property
  As close as possible from element-ui el-input
  https://github.com/ElemeFE/element/blob/dev/packages/input/src/input.vue

  Props:
  - value: same as el-input
  - type: the type of code editor:
    - "value" for a one line value-evaluted script (default)
        ex: $mycontext ? 12 : false
    - "program": a textarea script edit with line numbers
        ex: if ($mycontext) {
              @value = 12
            } else {
              @value = false
            }
    - "json" for a one line script turned into a JSON value
        ex: 12
  - varprefix: prefix of the parser, default is auto
      ex: {
            '$': 'context',
            '$$': 'contexts',
            '@': 'local',
            '@@': 'script',
            '': 'global',
          }
  - varstatus: status of the parser, default is auto (depedning on type)
      ex: {
            'global': 'callable',
            'script': 'freevar',
            'local': 'editable',
            'context': 'readonly',
            'contexts': 'readonly',
          }
  - ...: see <el-input> other attributes
-->

<template>
  <div :class="[
    multiline ? 'el-textarea' : 'el-input',
    'el-code-input',
    inputSize ? 'el-input--' + inputSize : '',
    {
      'is-disabled': inputDisabled,
      'el-input-group': $slots.prepend || $slots.append,
      'el-input-group--append': $slots.append,
      'el-input-group--prepend': $slots.prepend,
      'el-input--prefix': $slots.prefix || prefixIcon,
      'el-input--suffix': $slots.suffix || suffixIcon || clearable
    }
    ]"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
    v-clickoutside="handleClose"
  >
    <template v-if="!multiline">
      <!-- 前置元素 -->
      <div class="el-input-group__prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </div>
      <div class="el-input__inner el-code-input__placeholder">
        {{compiled ? '' : placeholder}}
      </div>
      <!-- eslint-disable vue/use-v-on-exact -->
      <div
        class="el-input__inner el-code-input__editable"
        :contenteditable="inputDisabled||readonly?'false':'true'"
        :tabindex="inputDisabled||readonly?-1:tabindex"
        v-bind="$attrs"
        :disabled="inputDisabled"
        :readonly="readonly"
        ref="input"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeyDown"
        @keydown.enter.prevent="e => handleEnter(e, true)"
        @keydown.ctrl.90.stop.prevent="handleCtrlZ"
        @keydown.ctrl.89.stop.prevent="handleCtrlY"
        @keydown.tab="handleOpen"
        @keydown.esc.stop.prevent="handleClose"
        @keydown.up="handleUp"
        @keydown.down="handleDown"
        :aria-label="label"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
      >
      <!--eslint-enable-->
        <span ref="once" data-prepos="0" v-once></span>
      </div>
      <!-- 前置内容 -->
      <span class="el-input__prefix" v-if="$slots.prefix || prefixIcon">
        <slot name="prefix"></slot>
        <i class="el-input__icon"
           v-if="prefixIcon"
           :class="prefixIcon">
        </i>
      </span>
      <!-- 后置内容 -->
      <span
        class="el-input__suffix"
        v-if="$slots.suffix || suffixIcon || showClear || validateState && needStatusIcon">
        <span class="el-input__suffix-inner">
          <template v-if="!showClear">
            <slot name="suffix"></slot>
            <i class="el-input__icon"
              v-if="suffixIcon"
              :class="suffixIcon">
            </i>
          </template>
          <i v-else
            class="el-input__icon el-icon-circle-close el-input__clear"
            @click="clear"
          ></i>
        </span>
        <i class="el-input__icon"
          v-if="validateState"
          :class="['el-input__validateIcon', validateIcon]">
        </i>
      </span>
      <!-- 后置元素 -->
      <div class="el-input-group__append" v-if="$slots.append">
        <slot name="append"></slot>
      </div>
    </template>
    <template v-else>
      <!-- <div class="el-input__inner el-code-input__placeholder"
        :style="textareaStyle">
        {{compiled ? '' : placeholder}}
      </div> -->
      <!-- eslint-disable vue/use-v-on-exact -->
      <div
        class="el-textarea__inner el-code-input__editable"
        :contenteditable="inputDisabled||readonly?'false':'true'"
        :tabindex="tabindex"
        v-bind="$attrs"
        :disabled="inputDisabled"
        :readonly="readonly"
        ref="input"
        :style="textareaStyle"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeyDown"
        @keydown.ctrl.enter.prevent="handleSubmit"
        @keydown.ctrl.90.stop.prevent="handleCtrlZ"
        @keydown.ctrl.89.stop.prevent="handleCtrlY"
        @keydown.tab="handleTab"
        @keydown.ctrl.space.prevent="handleOpen"
        @keydown.esc.stop.prevent="handleClose"
        @keydown.up="handleUp"
        @keydown.down="handleDown"
        @keydown.enter.stop.prevent="e => handleEnter(e, false)"
        :aria-label="label"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
      >
      <!--eslint-enable-->
        <div ref="once" data-prepos="0" v-once class="once"></div>
      </div>
    </template>
    <el-mobile-suggestions
      :width="suggestionWidth"
      visible-arrow
      :class="[popperClass ? popperClass : '']"
      :popper-options="popperOptions"
      :append-to-body="popperAppendToBody"
      ref="suggestions"
      :placement="placement"
      :id="id">
      <li
        v-for="(item, index) in suggestions"
        :key="index"
        :class="{'highlighted': highlightedIndex === index}"
        @click="select(item)"
        :id="`${id}-item-${index}`"
        role="option"
        :aria-selected="highlightedIndex === index"
      >
        <slot :item="item">
          <div class="el-code-autocomplete__item">
            <div class="el-code-autocomplete__definition">
              <span class="el-code-autocomplete__prefix">{{ item.prefix
              }}</span><span class="el-code-autocomplete__name">{{ item.left
                }}<span class="el-code-autocomplete__match">{{ item.match
                }}</span>{{ item.right
              }}</span><span class="el-code-autocomplete__call"
              v-html="htmlDoc(item.definition)"></span>
            </div>
            <div v-show="item.doc" class="el-code-autocomplete__doc"
              v-html="htmlDoc(item.doc)"></div>
          </div>
          <span class="el-code-autocomplete__count"
            v-if="'count' in item"> ({{item.count}})</span>
        </slot>
      </li>
    </el-mobile-suggestions>
  </div>
</template>
<script>
import emitter from 'element-ui/lib/mixins/emitter'
import merge from 'element-ui/lib/utils/merge'
import $ from 'jquery'
import Parser from '../../utils/parser.js'
import deeEqual from 'fast-deep-equal'
import { generateId } from 'element-ui/lib/utils/util'
import ElMobileSuggestions from './ElMobileSuggestions.vue'
import Clickoutside from 'element-ui/lib/utils/clickoutside'

// equivalent to spaces, contenteditable mixes those with normal spaces
const NBSP = String.fromCharCode(160)
const NBSP_REG = new RegExp(NBSP, 'g')
const NBSP_HTML_REG = new RegExp('&nbsp;', 'g')
const NEWLINE_REG = new RegExp('\\n', 'g')
// used for enmpty contenteditable, to avoid back to first line
const EMPTY_CHAR = String.fromCharCode(8203)
const EMPTY_REG = new RegExp(EMPTY_CHAR, 'g')
// this function compares strings while ignoring NBSP
function textEqual (a, b) {
  return (a || '').replace(NBSP_REG, ' ').replace(EMPTY_REG, '') ===
    (b || '').replace(NBSP_REG, ' ').replace(EMPTY_REG, '')
}
// removes every useless character
function cleanText (s) {
  return s.replace(EMPTY_REG, '').replace(NBSP_REG, ' ')
  // return s.replace(EMPTY_REG, '').replace(/\n/g, '').replace(NBSP_REG, ' ')
}

export default {
  name: 'ElCodeInput',
  componentName: 'ElInput',

  components: {
    ElMobileSuggestions,
  },

  directives: { Clickoutside },

  mixins: [emitter],
  inheritAttrs: false,
  inject: {
    elForm: {
      default: '',
    },
    elFormItem: {
      default: '',
    },
  },

  data () {
    return {
      // the current compiled value, from compile() call
      compiled: null,
      // the value of a "json" type input
      result: undefined,
      // if the script is valid
      valid: {
        type: Boolean,
        default: true,
      },
      validator: (rule, value, callback) => {
        callback(this.disabled || this.valid
          ? undefined : new Error('Script error'))
      },
      // the parser
      parser: null,
      program: null,
      // the history of edits, to handle ctrl + Z/Y
      history: {
        // a list of all the previous segments state
        list: [],
        // the position the next state should be inserted at,
        // potantially araising preiously cancelled state
        length: 0,
        // an object describing the last edit, in order to merge them
        edit: null,
      },
      // the last seen position of the cursor
      position: { absolute: -1 },
      // the edit that has just been made, if any
      edit: null,
      // true just after a user input
      fromInput: false,
      // the suggestion object
      suggestion: null,
      // copied from el-input
      textareaCalcStyle: {},
      hovering: false,
      focused: false,

      highlightedIndex: -1,
      suggestionDisabled: false,
    }
  },

  props: {
    // the value as a string
    value: {},
    // value, program or json
    type: {
      type: String,
      default: 'value',
    },
    // the possible variable prefix
    varprefix: Object,
    // the status of each type of variable
    varstatus: Object,
    // copied from el-input
    size: String,
    resize: String,
    form: String,
    disabled: Boolean,
    readonly: Boolean,
    autosize: {
      type: [Boolean, Object],
      default: false,
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    suffixIcon: String,
    prefixIcon: String,
    label: String,
    clearable: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: [String, Number],
      default: '0',
    },
    placeholder: String,
    // same as el-autocomplete
    popperClass: String,
    popperOptions: Object,
    maxlength: Number,
    minlength: Number,
    autofocus: Boolean,
    // fetchSuggestions: Function,
    triggerOnFocus: {
      type: Boolean,
      default: false,
    },
    customItem: String,
    placement: {
      type: String,
      default: 'bottom-start',
    },
    popperAppendToBody: {
      type: Boolean,
      default: true,
    },
    // // suggestion width
    // suggestionWidth: {
    //   type: Number,
    //   default: 150,
    // },
  },

  computed: {
    /* copied from el-input */
    _elFormItemSize () {
      return (this.elFormItem || {}).elFormItemSize
    },
    validateState () {
      return this.elFormItem ? this.elFormItem.validateState : ''
    },
    needStatusIcon () {
      return this.elForm ? this.elForm.statusIcon : false
    },
    validateIcon () {
      return {
        validating: 'el-icon-loading',
        success: 'el-icon-circle-check',
        error: 'el-icon-circle-close',
      }[this.validateState]
    },
    textareaStyle () {
      return merge({}, this.textareaCalcStyle, { resize: this.resize })
    },
    inputSize () {
      return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size
    },
    inputDisabled () {
      return this.disabled || (this.elForm || {}).disabled
    },
    showClear () {
      return this.clearable &&
          !this.inputDisabled &&
          !this.readonly &&
          this.compiled &&
          (this.focused || this.hovering)
    },
    activated () {
      return this.suggestion && this.suggestion.input &&
      this.suggestions.length > 0
    },
    suggestions () { return this.suggestion && this.suggestion.suggestions },
    // same as el-autocomplete
    suggestionVisible () {
      const suggestions = this.suggestions
      let isValidData = Array.isArray(suggestions) && suggestions.length > 0
      return (isValidData || this.loading) && this.activated
    },
    id () {
      return `el-autocomplete-${generateId()}`
    },
    suggestionWidth () {
      if (this.suggestions && this.suggestions.length &&
        this.suggestions[0].definition) {
        return 500
      } else return 150
    },
    multiline () {
      return this.type === 'program' || this.type === 'multiline'
    },
  },

  watch: {
    /* update content on value change */
    value (val) {
      // from el-input
      this.$nextTick(this.resizeTextarea)
      if (this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.change', [val])
      }
      // if the change is the consequence of a user input
      let fromInput = this.fromInput
      this.fromInput = false
      if (window._pause) return // for debugging
      // rerender the contenteditable div
      // a "json" input, rerender as JSON if the value is different
      if (this.type === 'json') {
        if (!deeEqual(val, this.result)) {
          this.compile(val === undefined ? '' : JSON.stringify(this.value),
            this.position.absolute)
        }
      // a normal input, rerender it
      } else if ((this.value || '') !== this.compiled) {
        this.compile(this.value || '', this.position)
      }
      // emit userupdate if this comes from a use input
      // display suggestion if the user it typing a variable
      if (fromInput) {
        this.handleSuggestion(true)
        this.$emit('userupdate')
      }
    },
    /* push new history entry after compile() has been called */
    compiled (val) {
      if (this.type === 'json') {
        this.pushHistory({ raw: val, value: this.result })
      } else this.pushHistory(val)
    },
    /* change history positon on cursor position change */
    position (val) {
      var hist = this.history.list[this.history.length - 1]
      if (hist.position.absolute !== val.absolute) {
        // updates current position in history (for the last redo)
        hist.position = val
        // stop edit merging
        this.history.edit = null
      }
      if (!val.type) return
      // saves a suggestion for later, if the user presses "down"
      this.handleSuggestion(false)
    },
    /* updates the suggestions on suggestionVisible change */
    suggestionVisible (val) {
      // needs to pass the preferred position of the suggestion
      this.broadcast('ElAutocompleteSuggestions', 'visible', [val, this.$refs.input.offsetWidth, this.suggestion && this.suggestion.x, this.suggestion && this.suggestion.y])
    },
  },

  methods: {
    // gives focus to the contenteditable and put the cursor at the end
    focus () {
      if (this.focused) return
      const selection = window.getSelection()
      let edit = this.$refs.input
      while (edit.lastChild) edit = edit.lastChild
      let range = document.createRange()
      range.setStart(edit, edit.nodeType === Node.TEXT_NODE ? edit.nodeValue.length : 0)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      this.handlePosition()
    },
    // inspired from el-input
    blur () {
      this.$refs.input.blur()
    },
    handleBlur (event) {
      this.focused = false
      this.$emit('blur', event)
      if (this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.blur', [this.value])
      }
    },
    resizeTextarea () {
      if (this.$isServer) return
      if (!this.multiline) return
      // those values should be found automatically
      const lineHeight = 21, paddingHeight = 12
      if (!this.autosize) {
        this.textareaCalcStyle = {
          minHeight: (lineHeight + paddingHeight) + 'px',
        }
        return
      }
      const minRows = this.autosize.minRows
      const maxRows = this.autosize.maxRows

      this.textareaCalcStyle = {
        minHeight: ((minRows || 1) * lineHeight + paddingHeight) + 'px',
        maxHeight: maxRows && (maxRows * lineHeight + paddingHeight) + 'px',
      }
    },
    handleFocus (event) {
      this.focused = true
      this.$emit('focus', event)
    },
    calcIconOffset (place) {
      let elList = [].slice.call(this.$el.querySelectorAll(`.el-input__${place}`) || [])
      if (!elList.length) return
      let el = null
      for (let i = 0; i < elList.length; i++) {
        if (elList[i].parentNode === this.$el) {
          el = elList[i]
          break
        }
      }
      if (!el) return
      const pendantMap = {
        suffix: 'append',
        prefix: 'prepend',
      }

      const pendant = pendantMap[place]
      if (this.$slots[pendant]) {
        el.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${this.$el.querySelector(`.el-input-group__${pendant}`).offsetWidth}px)`
      } else {
        el.removeAttribute('style')
      }
    },
    updateIconOffset () {
      this.calcIconOffset('prefix')
      this.calcIconOffset('suffix')
    },
    clear () {
      let value = this.type === 'json' ? undefined : ''
      this.handleClose()
      this.$emit('input', value)
      this.$emit('change', value)
      this.$emit('clear')
    },
    select (item) {
      // replace the cursor by the selected varibale
      // this.$refs.input.setVariable(this.query, item.value)
      // same as el-autocomplete
      this.$emit('select', item)
      let text = item.name, length = item.name.length
      if (item.cast === 'function') {
        text += '()'
        length += 1
      }
      let value = this.compiled.substr(0, this.suggestion.offset) +
        text + this.compiled.substr(this.suggestion.offset +
          this.suggestion.length)
      this.emitChange(value)
      this.setPosition(this.suggestion.offset + length)
      this.handleClose()
    },
    highlight (index) {
      if (!this.suggestionVisible) { return }
      if (index < 0) {
        this.highlightedIndex = -1
        return
      }
      if (index >= this.suggestions.length) {
        index = this.suggestions.length - 1
      }
      const suggestion = this.$refs.suggestions.$el.querySelector('.el-autocomplete-suggestion__wrap')
      const suggestionList = suggestion.querySelectorAll('.el-autocomplete-suggestion__list li')

      let highlightItem = suggestionList[index]
      let scrollTop = suggestion.scrollTop
      let offsetTop = highlightItem.offsetTop

      if (offsetTop + highlightItem.scrollHeight > (scrollTop + suggestion.clientHeight)) {
        suggestion.scrollTop += highlightItem.scrollHeight
      }
      if (offsetTop < scrollTop) {
        suggestion.scrollTop -= highlightItem.scrollHeight
      }
      this.highlightedIndex = index
      // this.$el.querySelector('.el-input__inner')
      this.$refs.input.setAttribute('aria-activedescendant', `${this.id}-item-${this.highlightedIndex}`)
    },
    // for "down" press, reopens the suggestion
    // if the cursor is at an interesting place
    handleDown (e) {
      if (this.activated) {
        e.preventDefault()
        this.highlight(this.highlightedIndex + 1)
      }
    },
    handleUp (e) {
      if (this.activated) {
        e.preventDefault()
        this.highlight(this.highlightedIndex - 1)
      }
    },
    handleOpen (e) {
      if (this.activated) {
        this.handleClose()
      } else if (this.position.absolute <= 0) {
        return // use the tab to go to the next field
      } else if (this.suggestions) {
        this.suggestion.input = true
        this.highlightedIndex = 0
      }
      e.preventDefault()
      e.stopPropagation()
    },
    handleEnter (e, submit) {
      if (this.suggestionVisible) {
        if (this.highlightedIndex >= 0 &&
          this.highlightedIndex < this.suggestions.length) {
          this.select(this.suggestions[this.highlightedIndex])
        }
      } else if (submit) this.handleSubmit()
      else {
        let position = this.position.absolute
        if (position < 0 || position > this.compiled) return
        let line = this.position.line, prefix = ['\n']
        if (typeof this.position.line === 'number') {
          let tabs = parseInt(this.compiled.split('\n')[line - 1]
            .match('^ *')[0].length / 2)
          $(this.$refs.once).find('.line[data-line="' +
            this.position.line + '"]').find(
            '.code-group-codeblock, .code-group-object, .code-group-array')
            .each(function () {
              let child = $(this)
              if (parseInt(child.attr('date-offset')) >= position) return false
              child = child.attr('data-text')
              if (child === '{' || child === '[') tabs++
              else if (child === '}' || child === ']') tabs--
            })
          while (tabs-- > 0) {
            prefix.push('  ')
          }
        }
        prefix = prefix.join('')
        let value = this.compiled.substr(0, position) + prefix +
          this.compiled.substr(position)
        this.emitChange(value)
        this.setPosition(position + prefix.length)
      }
    },
    handleClose () {
      if (this.suggestionVisible) {
        if (this.suggestion) this.suggestion.input = false
        this.highlightedIndex = -1
      }
    },

    // a recursive function used to parse all the text inside an element
    parseCode (el, accu) {
      // the accumulator of text
      accu = accu || []
      // go through all children
      for (let i = 0; i < el.childNodes.length; i++) {
        let child = el.childNodes[i]
        // the chikd is text, add it to the accu
        if (child.nodeType === Node.TEXT_NODE) {
          accu.push(child.textContent)
        // the child is neither text nor an element, pass
        } else if (child.nodeType !== Node.ELEMENT_NODE) {
          continue
        // the child is a <br> tag, add a newline to the accu
        } else if (child.tagName === 'BR') {
          accu.push('\n')
        // the child is an error-tooltip, pass
        } else if (child.classList.contains('error-tooltip')) {
          continue
        // recursively parse the child
        } else {
          this.parseCode(child, accu)
        }
      }
      return accu
    },
    // called on input events
    handleInput () {
      this.fromInput = true
      let input = this.$refs.input, once = this.$refs.once
      let pos = this.getPosition()
      // merge all lines of code
      // newline is used to know if a newline should be added after the line
      // this is mostly empiric way to deal with all browsers
      // 2 events increment newline:
      // - the line has been created by the browser (not original data)
      // - the line still has its prefix (.line-before and .line-number)
      // if newline > 0, a newline character is added before the line text
      // and if newline > 1, newline = 1 for the next line
      let newline = 0
      let value // the final value
      // a program, parse it line by line
      if (this.multiline) {
        value = []
        let first = true
        // a recursive function to parse the program
        // it's supposed to reaceive once and parse the line children
        // however contenteditable is more unpredictable
        let parseAll = (parent) => {
          for (let i = 0; i < parent.childNodes.length; i++) {
            let line = parent.childNodes[i]
            // a text node for some reason
            if (line.nodeType === Node.TEXT_NODE) {
              value.push(cleanText(line.textContent))
              first = false
            // finally the once, parse it
            } else if (line === once) {
              parseAll(line)
            // a line eventually
            } else if (line.classList.contains('line')) {
              let text = cleanText(this.parseCode(line).join(''))
              line = $(line)
              // special case for chrome which is inserting far too many lines
              if (text === '\n') text = ''
              // this line was added by the browser
              if (!line.data('original')) newline++
              // this line has still its prefix
              let before = line.children().first().filter('.line-before').text()
              let num = line.prev('.line-number')
              if (before && num.length) newline++
              // add a newline at the begin if needed
              if (!first && newline) value.push('\n')
              if (num.length) value.push(cleanText(num.text()))
              value.push(text)
              first = false
              // when breaking a line, firefox keeps the div for the second line
              // and creates a new div for the first line
              // so if both of the line markers, means the next div is also a line
              newline = newline > 1 ? 1 : 0
            // something else, read it
            } else if (!line.classList.contains('line-number')) {
              value.push(cleanText(line.innerText))
              first = false
            }
          }
        }
        parseAll(input)
        value = value.join('')
      // just a one line input, parse it nurmally and remove newlines
      } else {
        value = cleanText(this.parseCode(input).join(''))
          .replace(NEWLINE_REG, '')
      }
      // check if it was a mergeable edit (for history),
      // meaning the addition or suppression or one character
      // while not created / deleting a segment
      let edit = null
      // position was almost the same, at one character difference
      if (pos.right === this.position.right &&
            Math.abs(pos.left - this.position.left) ||
            (pos.left === this.position.left &&
            pos.right === this.position.right - 1)) {
        // diff = 1: character written
        // diff = -1: character deleted to the left
        // diff = 0: character deleted to the right
        let diff = pos.left - this.position.left
        // check that only one character was edited
        let mleft = Math.min(pos.left, this.position.left)
        let mright = Math.min(pos.right, this.position.right)
        if (!(mleft && !textEqual(value.substr(0, mleft),
          this.compiled.substr(0, mleft)) ||
          mright && !textEqual(value.substr(-mright),
            this.compiled.substr(-mright)))) {
          // when closing a "}" or "]" character on a newline
          // should remove 2 spaces for indentation
          if (diff === 1 &&
            value.substr(0, pos.absolute).match(/\n {2,}[}\]]$/)) {
            value = value.substr(0, pos.absolute - 3) +
              value.substr(pos.absolute - 1)
          // finally a proper mergeable edit
          } else {
            edit = {
              right: pos.right,
              left: pos.left,
              diff: diff,
            }
          }
        }
      }
      // saves changes
      this.edit = edit
      this.emitChange(value, pos)
    },
    /* recompile the script and emit the value change */
    emitChange (value, position) {
      if (!window._pause) {
        this.compile(value, position)
      }
      // for json, emit the json value, not the script
      if (this.type === 'json') value = this.result
      // emit changes
      this.$emit('input', value)
      this.$emit('change', value)
    },
    // submit to the parent form on enter press (only with oneline editors)
    handleSubmit () {
      $(this.$refs.once).closest('form').find('[type="submit"]').click()
    },
    // compiles and display the code
    compile (value, position) {
      let noerror = value.substr(0, '#!noerror\n'.length) === '#!noerror\n'
      // save a copy of the script in "compiled" for later comparison
      this.compiled = value
      // parse the script
      this.parser = new Parser(value, this.varprefix, this.varstatus)
      this.program = this.type === 'program' ? this.parser.parseProgram()
        : this.parser.parseValueProgram()
      let lines = [] // the list of tags that should be inserted
      let insert // a function to insert a new syntax from the parser
      // a program, write code line by line
      if (this.multiline) {
        let line = 0 // the current line number
        let div = null // the current div where to insert the syntax tags
        // creates a new line div, with a line number
        let newline = () => {
          line++
          // line number
          let numbers = $('<span></span>').addClass('line-number')
          for (let c of line + '') {
            numbers.append($('<span></span>').addClass('line-number-' + c))
          }
          // line content
          // .line-before is used such that the cursor doesn't go
          // all the way left to the line-number
          div = $('<div>').addClass('line').attr('data-line', line + '')
            .append($('<span></span>').addClass('line-before').text(EMPTY_CHAR))
            .data('original', true)
          // push the tags
          lines.push(numbers)
          lines.push(div)
        }
        // create the first line
        newline()
        // inserts a new syntax tag, possibly on several lines
        insert = (text, syntax) => {
          // the classes of the tag, by joining subtypes with "-"
          let cls = ['code']
          for (let t of syntax.type) {
            cls.push(cls[cls.length - 1] + '-' + t)
          }
          // the error class
          if (syntax.error) cls.push('compilation-error')
          cls = cls.join(' ')
          // list of the attributes of the tag
          let attrs = {
            'data-text': text,
            'data-type': syntax.type.join(' '),
            'data-offset': syntax.offset,
            'data-line': syntax.line,
            'data-col': syntax.col,
          }
          // split by line
          text = text.split('\n')
          text.forEach((s, i) => {
            // create the tag, use non breakable spaces
            let tag = $('<span>').text(s.replace(/ /g, NBSP))
              .addClass(cls).attr(attrs)
              .attr('data-length', s.length)
            // insert the error tooltip
            if (syntax.error) {
              tag.append($('<span>').addClass('error-tooltip')
                .css('display', 'none').text(syntax.error))
            }
            // add the tag
            div.append(tag)
            // create a newline (except if it's the last piece)
            if (i < text.length - 1) newline()
          })
        }
      // a one-line script, more simple to white
      } else {
        // an empty script, reset the input
        if (!value) {
          if (this.type === 'json') {
            this.result = undefined
          }
          this.valid = true
          // insert .line-before to avoid the cursor to touch the left border
          $(this.$refs.input).empty().append(
            $(this.$refs.once).empty().append(
              $('<span></span>').addClass('line-before').text(EMPTY_CHAR)))
          if (position) this.setPosition(position.absolute)
          this.$emit('valid', this.valid)
          return
        }
        // insert a syntax tag
        insert = (text, syntax) => {
          // the classes of the tag, by joining subtypes with "-"
          let cls = ['code']
          for (let t of syntax.type) {
            cls.push(cls[cls.length - 1] + '-' + t)
          }
          // the error class
          if (syntax.error) cls.push('compilation-error')
          cls = cls.join(' ')
          // list of the attributes of the tag
          let attrs = {
            'data-text': text,
            'data-type': syntax.type.join(' '),
            'data-offset': syntax.offset,
            'data-line': syntax.line,
            'data-col': syntax.col,
          }
          // create the tag, use non breakable spaces
          let tag = $('<span>').text(text.replace(/ /g, NBSP))
            .addClass(cls).attr(attrs)
            .attr('data-length', text.length)
          // insert the error tooltip
          if (syntax.error) {
            tag.append($('<span>').addClass('error-tooltip')
              .css('display', 'none').text(syntax.error))
          }
          // add the tag
          lines.push(tag)
        }
      }
      // check for validity
      let error = null
      // except "json" type, only check for compilation error
      if (this.type !== 'json') {
        this.valid = noerror || !this.program.error
      } else if (this.program.error) {
        this.valid = false
      // for "json" type, also check for execution errors
      } else {
        try {
          this.result = this.program.resolve({
            gobal: Parser.GLOBALS,
            script: {},
          })
          this.valid = true
        } catch (e) {
          this.valid = false
          error = e
        }
      }
      // insert all syntax tags
      let pos = 0
      for (let syntax of this.parser.syntax) {
        pos = syntax.offset + syntax.length
        // insert the execution error in the syntax
        if (error) {
          error.codestack.forEach((pos, index) => {
            if (syntax.offset === pos.offset) {
              syntax = {
                ...syntax,
                error: index ? 'called here' : error.message,
              }
            }
          })
        }
        // insert the tag from the syntax
        insert(value.substring(syntax.offset, pos), syntax)
      }
      // check if we shoud type UTF8 characters (string and comments)
      let once = $(this.$refs.once)
      let anchor = position && position.el[0]
      let utf8 = (anchor && anchor.attributes['data-type'] || {}).value
      utf8 = utf8 === 'string' || utf8 === 'comment'
      // typing utf8, try to just update the tag under the cursor
      // for the keyboard to not loose track
      if (utf8) {
        // compare texts
        // the text before the edit (from "data-text")
        let oldtext = (anchor.attributes['data-text'] || {}).value || ''
        // the text after the edit, as written by the prowser
        let newtext = ''
        for (let i = 0; i < anchor.childNodes.length; i++) {
          let child = anchor.childNodes[i]
          if (child.nodeType === Node.TEXT_NODE && child.textContent) {
            newtext = cleanText(child.textContent)
          }
        }
        if (!oldtext || !newtext) utf8 = false
        let diff = newtext.length - oldtext.length
        // update "data-text"  and "data-length"
        if (utf8 && oldtext !== newtext) {
          anchor.setAttribute('data-text', newtext)
          anchor.setAttribute('data-length', newtext.length)
        }
        // updates the offset and col of other elements after
        if (utf8 && diff) {
          let line = parseInt(anchor.attributes['data-line'].value)
          let offset = parseInt(anchor.attributes['data-offset'].value)
          let col = parseInt(anchor.attributes['data-col'].value)
          once.find('.code').each(function () {
            let v = parseInt(this.attributes['data-offset'].value)
            if (v > offset) this.setAttribute('data-offset', v + diff + '')
            if (line === parseInt(this.attributes['data-line'].value)) {
              v = parseInt(this.attributes['data-col'].value)
              if (v > col) this.setAttribute('data-col', v + diff + '')
            }
          })
        }
        // check that the result is expactly the one expected
        if (utf8) {
          let current = once.html().replace(NBSP_HTML_REG, ' ')
          let future = $('<div>').append(lines).html()
            .replace(NBSP_HTML_REG, ' ')
          if (current !== future) utf8 = false
        }
      }
      // else just replace
      if (!utf8) {
        $(this.$refs.input).empty().append(once.empty().append(lines))
        if (position) this.setPosition(position.absolute)
      }
      this.$emit('valid', this.valid)
    },
    // retrieves the current position of the cursor
    getPosition (anchor, offset) {
      const input = this.$refs.input, selection = window.getSelection()
      if (!anchor) {
        anchor = selection.anchorNode
        offset = selection.anchorOffset
      }
      anchor = anchor && $(anchor.nodeType === Node.TEXT_NODE
        ? anchor.parentNode : anchor)
      if (!anchor || !anchor.closest(input).length) {
        return { absolute: -1 }
      }
      let position = 0
      // look at every tag to find the current position
      let first = true, line = 1, newline = 0, out
      function aux (el) {
        // see handleInput for how line counting works
        if (el.className === 'line') {
          let ln = $(el)
          if (!ln.data('original')) newline++
          let before = ln.children().first().filter('.line-before').text()
          let num = ln.prev('.line-number').length
          if (before && num) newline++
          if (newline) {
            // a new line is also a character
            if (!first) {
              line++
              position++
            }
            newline = newline > 1 ? 1 : 0
          }
          first = false
        }
        // found the selected tag
        if (el === anchor[0]) {
          // special case, the cursor is on line-number, so count it as a new line too
          if (el.className === 'line-number' && !first) {
            line++
            position++
          }
          // found the absolute position
          let pos = position + el.innerText.substr(0, offset)
            .replace(EMPTY_REG, '').length
          let rect = selection.rangeCount ? selection.getRangeAt(0) : null
          rect = rect && rect.getClientRects()[0]
          let type = anchor.attr('data-type')
          type = type ? type.split(' ') : []
          let tagOffset = anchor.attr('data-offset')
          out = {
            absolute: pos, // absolute position
            line: line, // line number
            // offset of the syntax (lower than absolute)
            offset: tagOffset && parseInt(tagOffset),
            type: type, // type of the syntax
            left: pos, // how many characters on the left
            rect: rect, // position of the cursor on the screen
            el: anchor, // selected element
          }
        // text, adds it to the position
        }
        if (el.nodeType === Node.TEXT_NODE) {
          position += el.textContent.replace(EMPTY_REG, '').length
        // error-tooltip are ignored
        } else if (el.className === 'error-tooltip') {
        // recursive call
        } else {
          for (let i = 0; i < el.childNodes.length; i++) {
            aux(el.childNodes[i])
          }
        }
      }
      aux(input)
      // how many characters on the right
      out.right = position - out.absolute
      return out
    },
    // puts back the cursor at the desired position
    setPosition (position, endpos) {
      if (position < 0) return
      // the position of the end of the cursor, for selected text
      if (endpos === undefined) endpos = position
      const _this = this, once = $(this.$refs.once)
      const selection = window.getSelection(), range = document.createRange()
      let length = !this.compiled ? 0 : this.compiled.length
      // special case, the cursor position is beyond the script length
      // just find the last possible one
      if (position >= length) {
        let child = once.find('.code').last()
        let last = once.find('.code, .line-before').last()
        let node = last[0].childNodes[0]
        node = node && node.nodeType === Node.TEXT_NODE && node
        range.setStart(node || last[0], node ? node.textContent.length : 0)
        let rect = range.getClientRects()[0]
        let offset = child.attr('data-offset')
        offset = offset ? parseInt(offset) : 0
        let text = child.attr('data-text') || ''
        let type = child.attr('data-type')
        type = type ? type.split(' ') : []
        let line = parseInt(once.find('.line').last().data('data-line')) || 1
        this.position = {
          absolute: length,
          offset: offset,
          line: line,
          type: type,
          left: length,
          right: 0,
          text: text,
          rect: rect,
          el: child,
        }
        // position is already found, make it unfindable
        position = Infinity
      }
      // same problem for endpos
      if (endpos >= length) {
        let last = once.find('.code, .line-before').last()
        let node = last[0].childNodes[0]
        node = node && node.nodeType === Node.TEXT_NODE && node
        range.setEnd(node || last[0], node ? node.textContent.length : 0)
        // endpos is already found, make it unfindable
        endpos = Infinity
      }
      // check elements until the asolute position is found
      let pos = 0, line = 1
      once.find('.line, .code').each(function () {
        let child = $(this)
        if (child.is('.line')) {
          line = parseInt(child.attr('data-line')) || 1
          if (line > 1) pos++
          return
        }
        let length = parseInt(child.attr('data-length'))
        // position is on this element
        if (position <= pos + length) {
          // adds some useful infos about the cursor
          range.setStart(position > pos ? this.childNodes[0] : this,
            position - pos)
          let rect = range.getClientRects()[0]
          let offset = parseInt(child.attr('data-offset'))
          let text = child.attr('data-text')
          let type = child.attr('data-type').split(' ')
          // update position related to the current HTML
          _this.position = {
            absolute: position,
            offset: offset,
            line: line,
            type: type,
            left: position - offset,
            right: offset + length - position,
            text: text,
            rect: rect,
            el: child,
          }
          position = Infinity
        }
        // endpos is on this element
        if (endpos <= pos + length) {
          range.setEnd(endpos > pos ? this.childNodes[0] : this,
            endpos - pos)
          endpos = Infinity
        }
        pos += length
      })
      selection.removeAllRanges()
      selection.addRange(range)
    },
    // adds a new value in history, with the cursor position
    pushHistory (value) {
      // check that the new state is different
      let copy
      if (this.type === 'json') {
        if (this.history.length > 0 &&
          value.raw === this.history.list[this.history.length - 1].raw) return
        copy = { ...value, position: this.position }
      } else {
        if (this.history.length > 0 &&
          value === this.history.list[this.history.length - 1].value) return
        copy = { value: value, position: this.position }
      }
      // check if this edit can be merged with the previous one
      let edit = this.history.edit
      if (edit && this.edit &&
            edit.diff === this.edit.diff &&
            (edit.diff && edit.right === this.edit.right ||
            !edit.diff && edit.left === this.edit.left)) {
        this.history.list[this.history.length - 1] = copy
      } else {
        this.history.list[this.history.length] = copy
        this.history.length++
        this.history.edit = this.edit
      }
      // no possible CTRL+Y now, so cut the history array
      this.history.list.length = this.history.length
      this.edit = null
    },
    // restores a state from the history
    popHistory (redo) {
      // check which history item should be loaded
      if (redo) {
        if (this.history.length >= this.history.list.length) return
        this.history.length++
      } else {
        if (this.history.length < 2) return
        this.history.length--
      }
      // rebuild segments with global fields
      let hist = this.history.list[this.history.length - 1]
      // restore the state
      this.position = hist.position
      if (this.type === 'json') {
        this.compile(hist.raw, hist.position)
      }
      this.$emit('input', hist.value)
      this.$emit('change', hist.value)
    },
    // called by ctrl+Z, also manages ctrl+shift+Z
    handleCtrlZ (e) {
      this.popHistory(e.shiftKey)
    },
    // called by ctrl+Y
    handleCtrlY () {
      this.popHistory(true)
    },
    // called on document selectionchange event
    handlePosition () {
      let position = this.getPosition()
      if (!this.multiline || position.absolute < 0) {
        this.position = position
        return
      }
      // check if the new position is not on line-number or line-before
      // if it is, change the position to a valid place
      const selection = window.getSelection()
      const anchor = $(selection.anchorNode.nodeType === Node.TEXT_NODE
        ? selection.anchorNode.parentNode : selection.anchorNode)
      if (!window._pause && position.absolute >= 0 && !selection.anchorOffset &&
        (anchor.hasClass('line-before') && anchor.parent().data('original') ||
          anchor.hasClass('line-number'))) {
        // in some cases, we want to go back to the previous line
        this.setPosition(position.absolute -
          (position.absolute > 0 &&
            parseInt(anchor.parent().attr('data-line')) === this.position.line))
      } else this.position = position
    },
    // on keydownm, resets the tooltip such that they are not editable
    handleKeyDown () {
      $(this.$refs.once).find('.error-tooltip').css('display', 'none')
    },
    // add or remove identation
    handleTab (e) {
      // while there are suggestions, tab closes them
      if (this.suggestionVisible) {
        this.handleClose()
      } else if (this.position.absolute <= 0) {
        return // use the tab to go to the next field
      } else {
        // shift+tab removes identation instead of adding it
        const undo = e.shiftKey, selection = window.getSelection()
        // there's no selection, leave
        if (!selection.anchorNode || !selection.extentNode) return
        // find the position of the selection
        const anchor = this.getPosition(selection.anchorNode,
          selection.anchorOffset)
        const extend = this.getPosition(selection.extentNode,
          selection.extentOffset)
        // no  selection or line number, leave
        if (!anchor || !extend || typeof anchor.line !== 'number' ||
          typeof extend.line !== 'number') return
        // get positions range
        let frompos, topos
        if (anchor.absolute <= extend.absolute) {
          frompos = anchor
          topos = extend
        } else {
          frompos = extend
          topos = anchor
        }
        let lines = this.compiled.split('\n')
        // for all the concerned lines, add it (or remove it) 2 spaces
        for (let i = frompos.line; i <= topos.line; i++) {
          // shift+tab, check if 2 spaces can be removed
          if (undo) {
            let prefix = lines[i - 1].match(/^ {0,2}/)[0].length
            lines[i - 1] = lines[i - 1].substr(prefix)
            if (i === frompos.line) frompos.absolute -= prefix
            topos.absolute -= prefix
          // tab, just add 2 spaces
          } else {
            lines[i - 1] = '  ' + lines[i - 1]
            if (i === frompos.line) frompos.absolute += 2
            topos.absolute += 2
          }
        }
        let value = lines.join('\n')
        // update
        this.emitChange(value)
        this.setPosition(anchor.absolute, extend.absolute)
      }
      e.preventDefault()
      e.stopPropagation()
    },
    // calls to check for a new suggestion
    // input = true if it comes from a user input
    handleSuggestion (input) {
      let pos = this.position
      if (!pos.type || !pos.type.length) return
      let suggestion = this.parser.syntax.find(s => s.offset === pos.offset) || {}
      // suggestions found
      if (suggestion.suggestions) {
        suggestion = {
          input: input,
          type: suggestion.type,
          suggestions: suggestion.suggestions,
          offset: suggestion.offset + (suggestion.append ? suggestion.length : 0),
          length: suggestion.append ? 0 : suggestion.length,
          x: pos.el && pos.el.offset().left + pos.el.width() / 2,
          y: pos.el && pos.el.offset().top + pos.el.height(),
        }
      } else suggestion = null
      // no suggestion for the cursor, close the suggestion
      if (!suggestion) this.suggestion = null
      // suggestion on input, open suggestions
      else if (input) this.suggestion = suggestion
      // no previous suggestion opened, keep suggestion for "down" pressed
      else if (!this.suggestion || !this.suggestion.input) {
        this.suggestion = suggestion
      // suggestion on a different variable, close and replace the current one
      } else if (suggestion.key !== this.suggestion.key) {
        this.suggestion = suggestion
      }
    },
    /* creates the HTML documentation for suggestions */
    htmlDoc (doc) {
      if (!doc) return ''
      // replace &, < and > by their HTML equivalent
      return doc.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;').replace(/>/g, '&lt;')
        // *...* are variables (bold)
        .replace(/\*([^*\n]+)\*/g, '<code class="var">$1</code>')
        // `...` are code (monospace)
        .replace(/`([^`\n]+)`/g, '<code>$1</code>')
        // replace newlines by <br/>
        .replace(/\n/g, '<br/>')
    },
  },

  created () {
    // push the first state of the history
    if (this.type === 'json') {
      let raw = this.value === undefined ? '' : JSON.stringify(this.value)
      this.pushHistory({ raw: raw, value: this.value })
    } else {
      this.pushHistory(this.value || '')
    }
    // listen selectionchange
    document.addEventListener('selectionchange', this.handlePosition)
    // copied from el-input
    this.$on('inputSelect', this.select)
  },

  mounted () {
    // render the contenteditable for the first time
    if (this.type === 'json') {
      let raw = this.value === undefined ? '' : JSON.stringify(this.value)
      this.compile(raw)
    } else {
      this.compile(this.value || '')
    }
    // copied from el-input
    this.resizeTextarea()
    this.updateIconOffset()
    // shows the tooltip when over it
    $(this.$refs.input).on('mouseenter', '.compilation-error', (e) => {
      $(e.currentTarget).find('.error-tooltip').css('display', 'block')
    })
    // hides it after
    $(this.$refs.input).on('mouseleave', '.compilation-error', (e) => {
      $(e.currentTarget).find('.error-tooltip').css('display', 'none')
    })

    this.$on('item-click', item => {
      this.select(item)
    })
    // let $input = this.$el.querySelector('.el-input__inner')
    let $input = this.$refs.input
    $input.setAttribute('role', 'textbox')
    $input.setAttribute('aria-autocomplete', 'list')
    $input.setAttribute('aria-controls', 'id')
    $input.setAttribute('aria-activedescendant', `${this.id}-item-${this.highlightedIndex}`)
  },

  // copied from el-input
  updated () {
    this.$nextTick(this.updateIconOffset)
  },

  beforeDestroy () {
    // stop listening selectionchange
    document.removeEventListener('selectionchange', this.handlePosition)
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";
  @import "../../variables.scss";

  /* avoid weird CSS bugs with height */
  .el-input.el-code-input {
    height: $--input-height;
  }
  /* to enable "scroll on select" */
  .el-input.el-code-input:not(.is-disabled) {
    display: inline-table; // to enable "scroll on select"
  }
  /* forces the text on the left, leaves the content transparent for the placeholder */
  .el-input__inner.el-code-input__editable {
    background-color: transparent !important;
  }
  .el-input__inner.el-code-input__editable,
  .el-textarea__inner.el-code-input__editable {
    text-align: start;
    font-family: monospace;
  }
  .el-textarea__inner.el-code-input__editable {
    overflow: auto;
  }
  /* the placeholder fills the background of the input */
  .el-input__inner.el-code-input__placeholder,
  .el-textarea__inner.el-code-input__placeholder  {
    position: absolute;
    text-align: start;
    border-color: transparent !important;
    color: $--color-text-placeholder;
    z-index: -100;
  }
  /* ignores new lines in input */
  /*.el-input__inner.el-code-input__editable {
    white-space: pre;
    overflow: hidden;
  }*/
  .el-input__inner.el-code-input__editable .code {
    line-height: normal;
  }
  .el-input__inner.el-code-input__editable br {
    display:none;
  }
  /* pre gives more problem than it solves */
  /*.el-textarea__inner.el-code-input__editable {
    white-space: pre-wrap;
  }*/
  /*leaves place for the line number*/
  .el-code-input .line {
    min-height: 21px;
    margin-left: 35px;
  }
  /* the line number */
  .el-code-input .line-number {
    display: block;
    float: left;
    width: 30px;
    text-align: right;
    color: $--color-text-placeholder;
  }
  /* the digits of the line number */
  .el-code-input .line-number-0:before { content: '0'; }
  .el-code-input .line-number-1:before { content: '1'; }
  .el-code-input .line-number-2:before { content: '2'; }
  .el-code-input .line-number-3:before { content: '3'; }
  .el-code-input .line-number-4:before { content: '4'; }
  .el-code-input .line-number-5:before { content: '5'; }
  .el-code-input .line-number-6:before { content: '6'; }
  .el-code-input .line-number-7:before { content: '7'; }
  .el-code-input .line-number-8:before { content: '8'; }
  .el-code-input .line-number-9:before { content: '9'; }

  /* syntax coloring */
  .el-code-input .code {
    color: #333333;
  }
  .el-code-input .code.code-operator {
    color: #f97b58;
  }
  .el-code-input .code.code-operator-dot,
  .el-code-input .code.code-operator-getter {
    color: #5fb4b4;
  }
  .el-code-input .code.code-separator {
    color: #ac7a68;
  }
  .el-code-input .code.code-number {
    color: #f97b58;
  }
  .el-code-input .code.code-variable-script,
  .el-code-input .code.code-variable-local,
  .el-code-input .code.code-variable-argument {
    color: #6699cc;
  }
  .el-code-input .code.code-variable-context,
  .el-code-input .code.code-variable-contexts {
    color: #5fb4b4;
  }
  .el-code-input .code.code-string {
    color: #99c794;
  }
  .el-code-input .code.code-block {
    color: #c695ca;
  }
  .el-code-input .code.code-literal {
    color: #ec5f66;
  }
  .el-code-input .code.code-comment {
    color: #999999
  }

  .el-code-input .code.compilation-error {
    position: relative;
    display: inline-block;
    border-bottom: 2px solid red;
    /*text-decoration: underline wavy red;*/
  }
  .el-code-input .code.runtime-error {
    position: relative;
    display: inline-block;
    border-bottom: 2px solid purple;
    /*text-decoration: underline wavy red;*/
  }
  .el-code-input .code.compilation-error .error-tooltip {
    display: none;
    width: auto;
    background-color: #555;
    color: #fff;
    text-align: center;
    border-radius: 4px;
    padding: 0 15px;
    position: absolute;
    z-index: $tooltip-z-index;
    bottom: 98%;
    left: 50%;
    margin-left: -10px;
    -webkit-transition: opacity 0.3s;
    transition: opacity 0.3s;
    white-space: nowrap;
    font-size: 13px;
    line-height: 28px;
  }
  .el-code-input .code.compilation-error .error-tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 10%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #555 transparent transparent transparent;
  }
  /*.el-code-input .code.compilation-error:hover .error-tooltip {
    display: inline-block;
    visibility: visible;
    opacity: 1;
  }*/
  .el-code-autocomplete__item {
    line-height: normal;
    margin-top: 5px;
    margin-bottom: 5px;
    white-space: normal;
  }
  .el-code-autocomplete__name {
    font-weight: bold;
    .el-code-autocomplete__match {
      text-decoration: underline;
    }
  }
  .el-code-autocomplete__call {
    font-style: italic;
    code {
      font-style: normal;
    }
  }
  .el-code-autocomplete__doc {
    color: $--color-text-secondary;
    font-size: 12px;
  }
  .el-code-autocomplete__item code.var {
    font-weight: bold;
  }
</style>
